import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCalendarAlt, 
  FaInfoCircle, 
  FaCar, 
  FaUser, 
  FaEuroSign, 
  FaTimes, 
  FaCheckCircle, 
  FaTimesCircle,
  FaFileInvoiceDollar,
  FaExchangeAlt,
  FaWrench,
  FaReceipt
} from 'react-icons/fa';
import moment from 'moment';
import {rentals as RentalsApi, handoverService, cars } from '../../../services/api'; 

// Utility functions for calculating rental duration and additional options pricing
const calculateRentalDays = (pickupDateTime, returnDateTime) => {
  const pickup = new Date(pickupDateTime);
  const returnDate = new Date(returnDateTime);
  const diffInMs = returnDate - pickup;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24)); // Round up to ensure minimum 1 day
  return Math.max(1, diffInDays); // Minimum 1 day
};

const calculateAdditionalOptionsPrice = (additionalOptions, rentalDays) => {
  if (!additionalOptions || additionalOptions.length === 0) return 0;
  return additionalOptions.reduce((sum, option) => sum + (option.price * rentalDays), 0);
};

// InfoCard component for displaying vehicle information
const InfoCard = ({ label, value, icon, valueColor = 'text-gray-900' }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <dt className="text-sm font-medium text-gray-500 mb-1">{label}</dt>
        <dd className={`text-lg font-semibold ${valueColor} truncate`} title={value}>
          {value || 'N/A'}
        </dd>
      </div>
      {icon && (
        <div className="ml-3 text-2xl opacity-70">
          {icon}
        </div>
      )}
    </div>
  </div>
);

const RentalDetailsModal = ({ isOpen, onClose, rental }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [handovers, setHandovers] = useState([]);
  const [isLoadingHandovers, setIsLoadingHandovers] = useState(false);
  const [assignedVehicleDetails, setAssignedVehicleDetails] = useState(null);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);

  useEffect(() => {
    if (isOpen && rental?._id) {
      fetchHandovers();
    } else {
      setHandovers([]); // Clear handovers when modal closes or rental changes
    }
  }, [isOpen, rental?._id]); // Ensure dependency on rental._id

  useEffect(() => {
    if (isOpen && rental?.vehicleInstanceId) {
      fetchAssignedVehicleDetails(rental.vehicleInstanceId);
    } else {
      setAssignedVehicleDetails(null); // Clear vehicle details if no instanceId or modal closed
    }
  }, [isOpen, rental?.vehicleInstanceId]); // Dependency on vehicleInstanceId

  const fetchHandovers = async () => {
    if (!rental?._id) return;
    
    setIsLoadingHandovers(true); // Use specific loading state
    try {
      const response = await handoverService.getHandoversByRental(rental._id);
      setHandovers(response.data);
      console.log('Fetched handovers:', response.data);
    } catch (error) {
      console.error('Error fetching handovers:', error);
    } finally {
      setIsLoadingHandovers(false); // Use specific loading state
    }
  };

  const fetchAssignedVehicleDetails = async (vehicleInstanceId) => {
    if (!vehicleInstanceId) return;
    setIsLoadingVehicle(true);
    try {
    // First we need to get the car model from the vehicle id
    const carResponse = await cars.getCarModelFromVehicle(vehicleInstanceId);
    // Then we can get the detailed vehicle information
    const response = await cars.getVehicleById(carResponse.data._id, vehicleInstanceId);
      setAssignedVehicleDetails(response.data);
      console.log('Fetched assigned vehicle details:', response.data);
    } catch (error) {
      console.error('Error fetching assigned vehicle details:', error);
      setAssignedVehicleDetails(null); // Clear on error
    } finally {
      setIsLoadingVehicle(false);
    }
  };

  if (!isOpen || !rental) return null;

  // Modal backdrop with animation
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <FaInfoCircle className="mr-2 text-blue-600" />
              Rental Details: {rental.customer.firstName} {rental.customer.lastName} ({rental._id})
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs Navigation */}
          <div className="px-6 py-2 border-b border-gray-200 bg-white">
            <div className="flex space-x-4 overflow-x-auto">
              <TabButton 
                label="Overview" 
                icon={<FaInfoCircle />} 
                active={activeTab === 'overview'} 
                onClick={() => setActiveTab('overview')} 
              />
              <TabButton 
                label="Customer" 
                icon={<FaUser />} 
                active={activeTab === 'customer'} 
                onClick={() => setActiveTab('customer')} 
              />
              <TabButton 
                label="Vehicle" 
                icon={<FaCar />} 
                active={activeTab === 'vehicle'} 
                onClick={() => setActiveTab('vehicle')} 
              />
              <TabButton 
                label="Pricing" 
                icon={<FaEuroSign />} 
                active={activeTab === 'pricing'} 
                onClick={() => setActiveTab('pricing')} 
              />
              <TabButton 
                label="Handovers" 
                icon={<FaExchangeAlt />} 
                active={activeTab === 'handovers'} 
                onClick={() => setActiveTab('handovers')} 
              />
              <TabButton 
                label="AADE" 
                icon={<FaReceipt />} 
                active={activeTab === 'aade'} 
                onClick={() => setActiveTab('aade')} 
              />
            </div>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <OverviewTab rental={rental} />
            )}
            
            {activeTab === 'customer' && (
              <CustomerTab rental={rental} />
            )}
            
            {activeTab === 'vehicle' && (
              <VehicleTab 
                rental={rental} 
                assignedVehicleDetails={assignedVehicleDetails} 
                isLoadingVehicle={isLoadingVehicle} 
              />
            )}
            
            {activeTab === 'pricing' && (
              <PricingTab rental={rental} />
            )}
            
            {activeTab === 'handovers' && (
              <HandoversTab rental={rental} handovers={handovers} isLoading={isLoadingHandovers} />
            )}
            
            {activeTab === 'aade' && (
              <AADETab rental={rental} />
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Tab Button Component
const TabButton = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
      active 
        ? 'bg-blue-100 text-blue-700' 
        : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    <span className="mr-2">{icon}</span>
    {label}
  </button>
);

// Overview Tab
const OverviewTab = ({ rental }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Rental Status Card */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-700 mb-3">Rental Status</h3>
        <div className="flex items-center">
          <StatusBadge status={rental.status} />
          <span className="ml-2 text-sm font-medium text-gray-600">
            Created: {moment(rental.createdAt).format('MMM D, YYYY')}
          </span>
        </div>
      </div>
      
      {/* Total Price Card */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-700 mb-3">Price Details</h3>
        {(() => {
          const rentalDays = calculateRentalDays(rental.pickupDateTime, rental.returnDateTime);
          const additionalOptionsPrice = calculateAdditionalOptionsPrice(rental.additionalOptions, rentalDays);
          const basePrice = rental.totalPrice - (rental.additionalOptionsPrice || 0);
          const finalTotalPrice = basePrice + additionalOptionsPrice;
          
          return (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price:</span>
                <span className="font-semibold text-gray-900">€{basePrice.toFixed(2)}</span>
              </div>
              {additionalOptionsPrice > 0 && (
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Additional Options:</span>
                  <span className="font-semibold text-gray-900">€{additionalOptionsPrice.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-600">Total Price:</span>
                <span className="font-semibold text-gray-900">€{finalTotalPrice.toFixed(2)}</span>
              </div>
              {rental.advancePayment > 0 && (
                <>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600">Advance Payment:</span>
                    <span className="font-semibold text-green-600">€{rental.advancePayment}</span>
                  </div>
                  <div className="flex justify-between mt-1 pt-1 border-t border-gray-200">
                    <span className="text-gray-600">Balance Due:</span>
                    <span className="font-semibold text-blue-600">€{(finalTotalPrice - rental.advancePayment).toFixed(2)}</span>
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
    </div>
    
    {/* Rental Period Card */}
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h3 className="font-medium text-gray-700 mb-3">Rental Period</h3>
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex items-start space-x-3">
          <div className="bg-green-100 p-2 rounded-full">
            <FaCalendarAlt className="text-green-600 w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Pickup</p>
            <p className="text-sm text-gray-700">{moment(rental.pickupDateTime).format('MMM D, YYYY - HH:mm')}</p>
            <p className="text-xs text-gray-600 mt-1">{rental.pickupLocation}</p>
          </div>
        </div>
        
        <div className="hidden md:block self-center">
          <div className="w-12 h-0.5 bg-gray-300 relative">
            <div className="absolute h-2 w-2 bg-gray-400 rounded-full -top-0.5 -right-1"></div>
            <div className="absolute h-2 w-2 bg-gray-400 rounded-full -top-0.5 -left-1"></div>
          </div>
        </div>
        
        <div className="flex items-start space-x-3">
          <div className="bg-red-100 p-2 rounded-full">
            <FaCalendarAlt className="text-red-600 w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Return</p>
            <p className="text-sm text-gray-700">{moment(rental.returnDateTime).format('MMM D, YYYY - HH:mm')}</p>
            <p className="text-xs text-gray-600 mt-1">{rental.returnLocation}</p>
          </div>
        </div>
      </div>
    </div>
      {/* Additional Options and Notes */}
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h3 className="font-medium text-gray-700 mb-3">Additional Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Options</h4>
          {rental.additionalOptions && rental.additionalOptions.length > 0 ? (() => {
            const rentalDays = calculateRentalDays(rental.pickupDateTime, rental.returnDateTime);
            
            return (
              <div className="space-y-2">
                <p className="text-xs text-gray-600">
                  {rentalDays} day{rentalDays > 1 ? 's' : ''} rental
                </p>
                <div className="flex flex-wrap gap-2">
                  {rental.additionalOptions.map((option, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {option.name} - €{option.price.toFixed(2)}/day (€{(option.price * rentalDays).toFixed(2)} total)
                    </span>
                  ))}
                </div>
              </div>
            );          })() : (
            <p className="text-sm text-gray-500">No additional options selected</p>
          )}
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
          <p className="text-sm text-gray-600 bg-white p-2 rounded border border-gray-200 min-h-[60px]">
            {rental.additionalNotes || "No additional notes"}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Customer Tab
const CustomerTab = ({ rental }) => (
  <div className="space-y-6">
    <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
      <div className="flex items-start space-x-4 mb-4">
        <div className="bg-blue-100 p-3 rounded-full">
          <FaUser className="text-blue-600 w-6 h-6" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {rental.customer.firstName} {rental.customer.lastName}
          </h3>
          <p className="text-gray-600">{rental.customer.email}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Contact Information</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Phone:</span>
              <span className="text-sm text-gray-900">{rental.customer.phoneNumber}</span>
            </div>
            {rental.customer.alternatePhone && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Alternate Phone:</span>
                <span className="text-sm text-gray-900">{rental.customer.alternatePhone}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Age:</span>
              <span className="text-sm text-gray-900">{rental.customer.age || "N/A"}</span>
            </div>
          </div>
        </div>
        

      </div>
      
      {rental.customer.address && (rental.customer.address.street || rental.customer.address.city || rental.customer.address.postalCode ) && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Address</h4>
          <p className="text-sm text-gray-900">
            {[rental.customer.address.street, rental.customer.address.city, rental.customer.address.postalCode, rental.customer.address.country]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      )}
    </div>
  </div>
);

// Vehicle Tab
const VehicleTab = ({ rental, assignedVehicleDetails, isLoadingVehicle }) => {
  console.log('Assigned Vehicle Details:', assignedVehicleDetails);
  console.log('Rental:', rental);
  const hasAssignedVehicle = rental.licensePlate || (rental.vehicleInstanceId && rental.vehicleInstanceId.length > 0);
  const vehicleToDisplay = assignedVehicleDetails || rental.car; // Fallback to rental.car if specific details not loaded/available

  return (
    <div className="space-y-6">
      {/* Vehicle Assignment Status Banner */}
      <div 
        className={`p-4 rounded-lg flex items-center space-x-3 border ${
          hasAssignedVehicle 
            ? 'bg-green-50 border-green-200' 
            : 'bg-yellow-50 border-yellow-200'
        }`}
      >
        {hasAssignedVehicle ? (
          <FaCheckCircle className="w-6 h-6 text-green-600" />
        ) : (
          <FaInfoCircle className="w-6 h-6 text-yellow-600" />
        )}
        <div>
          <h3 className={`text-md font-semibold ${hasAssignedVehicle ? 'text-green-800' : 'text-yellow-800'}`}>
            {hasAssignedVehicle ? 'Vehicle Assigned' : 'Vehicle Not Yet Assigned'}
          </h3>
          <p className={`text-sm ${hasAssignedVehicle ? 'text-green-700' : 'text-yellow-700'}`}>
            {hasAssignedVehicle 
              ? 'Details for the specifically assigned vehicle are shown below.' 
              : 'A specific vehicle has not been assigned to this rental yet. General model information may be shown.'}
          </p>
        </div>
      </div>

      {/* Vehicle Details Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
          <div className="bg-blue-500 p-3 rounded-full">
            <FaCar className="text-white w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {assignedVehicleDetails ? 'Assigned Vehicle Details' : 'Vehicle Details'}
            </h3>
            <p className="text-sm text-gray-600">
              {rental?.car.make} {rental?.car.model} ({rental?.car.year})
            </p>
          </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoadingVehicle && (
            <div className="text-center py-8">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full mb-3"></div>
            <p className="text-gray-500">Loading assigned vehicle details...</p>
          </div>
            </div>
          )}

          {/* Vehicle Information Grid */}
          {!isLoadingVehicle && (
            <div className="p-6">
          {/* License Plate Banner */}
          <div className="mb-6">
            <div className={`p-4 rounded-lg border-2 ${
              rental.licensePlate 
            ? 'bg-green-50 border-green-200' 
            : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${
                rental.licensePlate ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                <FaCar className={`w-5 h-5 ${
              rental.licensePlate ? 'text-green-600' : 'text-orange-600'
                }`} />
              </div>
              <div>
                <h4 className={`font-medium ${
              rental.licensePlate ? 'text-green-800' : 'text-orange-800'
                }`}>
              {rental.licensePlate ? 'Vehicle Assigned' : 'Vehicle Not Yet Assigned'}
                </h4>
                <p className={`text-sm ${
              rental.licensePlate ? 'text-green-700' : 'text-orange-700'
                }`}>
              {rental.licensePlate 
                ? `License Plate: ${rental.licensePlate}` 
                : 'No license plate assigned yet'}
                </p>
              </div>
            </div>
            {rental.vehicleInstanceId && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                ID: {rental.vehicleInstanceId}
              </span>
            )}
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoCard label="Make" value={rental.car.make} />
              <InfoCard label="Model" value={rental.car.model} />
              <InfoCard label="Year" value={rental.car.year} />
              <InfoCard label="Category" value={rental.car.category} />
              <InfoCard label="Exterior Color" value={rental.car.exteriorColor} />
              <InfoCard label="Interior Color" value={rental.car.interiorColor} />
            </div>
          </div>

          {/* Technical Specifications Section */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              Technical Specifications
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoCard label="Fuel Type" value={rental.car.fuelType} />
              <InfoCard label="Transmission" value={rental.car.transmission} />
              <InfoCard label="Drivetrain" value={rental.car.drivetrain} />
              <InfoCard label="Doors" value={rental.car.doors} />
              <InfoCard label="Seats" value={rental.car.seats} />
              <InfoCard label="Suitcases" value={rental.car.suitcases} />
            </div>
          </div>

          {/* Vehicle Condition Section */}
          {(assignedVehicleDetails?.mileage || assignedVehicleDetails?.vin || rental.car.airConditioner !== undefined) && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
            Vehicle Condition & Features
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {assignedVehicleDetails?.mileage && (
              <InfoCard 
                label="Mileage" 
                value={`${assignedVehicleDetails.mileage} km`} 
                icon="📊"
              />
            )}
            {assignedVehicleDetails?.vin && (
              <InfoCard 
                label="VIN" 
                value={assignedVehicleDetails.vin} 
                icon="🔢"
              />
            )}
            <InfoCard 
              label="Air Conditioning" 
              value={rental.car.airConditioner ? 'Yes' : 'No'} 
              icon="❄️"
              valueColor={rental.car.airConditioner ? 'text-green-600' : 'text-red-600'}
            />
              </div>
            </div>
          )}
          
            {rental.car.features && rental.car.features.length > 0 && (
              <div className="md:col-span-2 mt-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
                <div className="flex flex-wrap gap-2">
                  {rental.car.features.map((feature, index) => (
                    <span key={index} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Specifications */}
            {rental.car.specifications && Object.keys(rental.car.specifications).length > 0 && (
              <div className="md:col-span-2 mt-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Specifications:</h4>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  {Object.entries(rental.car.specifications).map(([key, value]) => (
                    value && (
                      <div key={key} className="flex justify-between py-1">
                        <span className="text-sm text-gray-600 capitalize">{key}:</span>
                        <span className="text-sm text-gray-800">{value}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {rental.car.description && (
              <div className="md:col-span-2 mt-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Description:</h4>
                <p className="text-sm text-gray-800 bg-white p-3 rounded-lg border border-gray-200">
                  {rental.car.description}
                </p>
              </div>
            )}
          </div>
        )}
        
        {!isLoadingVehicle && !assignedVehicleDetails && rental.vehicleInstanceId && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-center">
            <p className="text-sm text-red-700">
              Could not load specific details for the assigned vehicle (ID: {rental.vehicleInstanceId}). 
              Displaying general model information.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};



// Pricing Tab
const PricingTab = ({ rental }) => (
  <div className="space-y-6">
    <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Price Breakdown
      </h3>

      {/* Base Rental Prices */}
      <div className="space-y-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700">Daily Rates</h4>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Rate</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rental.priceBreakdown.map((period, index) => (
                <tr key={index}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {moment(period.startDate).format('MMM D, YYYY')} - {moment(period.endDate).format('MMM D, YYYY')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">€{period.pricePerDay}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{period.numberOfDays}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">€{period.totalForPeriod}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>      {/* Totals and Calculations */}
      <div className="space-y-2 mt-6">
        {rental.additionalOptions && rental.additionalOptions.length > 0 && (() => {
          const rentalDays = calculateRentalDays(rental.pickupDateTime, rental.returnDateTime);
          const totalAdditionalOptionsPrice = calculateAdditionalOptionsPrice(rental.additionalOptions, rentalDays);
          
          return (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Additional Options ({rentalDays} day{rentalDays > 1 ? 's' : ''}):</span>
                <span className="text-gray-900">€{totalAdditionalOptionsPrice.toFixed(2)}</span>
              </div>
              {rental.additionalOptions.map((option, index) => (
                <div key={index} className="flex justify-between text-xs text-gray-500 ml-4">
                  <span>• {option.name}</span>
                  <span>€{option.price.toFixed(2)}/day × {rentalDays} = €{(option.price * rentalDays).toFixed(2)}</span>
                </div>
              ))}
            </div>
          );        })()}
        
        {rental.discountAmount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount {rental.couponCode ? `(${rental.couponCode})` : ''}:</span>
            <span>-€{rental.discountAmount}</span>
          </div>
        )}
        
        <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
          <span className="text-gray-600">Before VAT:</span>
          <span className="text-gray-900">€{(rental.totalPrice / 1.24).toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">VAT (24%):</span>
          <span className="text-gray-900">€{(rental.totalPrice - rental.totalPrice / 1.24).toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-base font-medium pt-2 border-t border-gray-200">
          <span className="text-gray-700">Total Price (inc. VAT):</span>
          <span className="text-gray-900">€{rental.additionalOptionsPrice ? rental.totalPrice - rental.additionalOptionsPrice + calculateAdditionalOptionsPrice(rental.additionalOptions, calculateRentalDays(rental.pickupDateTime, rental.returnDateTime)) : rental.totalPrice}
</span>
        </div>
        
        {rental.advancePayment > 0 && (
          <>
            <div className="flex justify-between text-sm text-amber-600 pt-2 border-t border-gray-200">
              <span>Advance Payment:</span>
              <span>-€{rental.advancePayment}</span>
            </div>
            <div className="flex justify-between text-base font-medium pt-2 mt-1 bg-blue-50 p-2 rounded-md border border-blue-100">
              <span>Balance Due:</span>
              <span>€{(rental.totalPrice - rental.advancePayment).toFixed(2)}</span>
            </div>
          </>
        )}
        
        {rental.commissionAmount > 0 && (
          <div className="flex justify-between text-sm pt-2 mt-4 border-t border-gray-200">
            <span className="text-gray-600">Commission:</span>
            <span className="text-gray-900">€{rental.commissionAmount.toFixed(2)}</span>
          </div>
        )}
          {/* Pre-Invoice PDF Button */}
        {rental.preInvoicePdfUrl && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={async () => {
                try {
                  const response = await RentalsApi.getPreInvoicePdf(rental._id);
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const pdfUrl = window.URL.createObjectURL(blob);
                  window.open(pdfUrl, '_blank');
                  // Clean up the object URL after a short delay
                  setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 1000);
                } catch (error) {
                  console.error('Error opening PDF:', error);
                  alert('Failed to open PDF. Please try again.');
                }
              }}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 flex items-center w-full justify-center"
            >
              <FaFileInvoiceDollar className="w-4 h-4 mr-2" />
              View Pre-Invoice PDF
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Handovers Tab
const HandoversTab = ({ rental, handovers, isLoading }) => (
  <div className="space-y-6">
    {isLoading ? (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    ) : handovers.length === 0 ? (
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
        <p className="text-yellow-700">No handover records found for this rental.</p>
      </div>
    ) : (
      <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Handover Records
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {handovers.map((handover) => (
            <div key={handover._id} className={`p-4 rounded-lg border ${
              handover.handoverType === 'pickup' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className={`text-base font-medium ${
                    handover.handoverType === 'pickup' ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {handover.handoverType === 'pickup' ? 'Pickup Handover' : 'Return Handover'}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {moment(handover.handoverDate || handover.createdAt).format('MMM D, YYYY - HH:mm')}
                  </p>
                </div>
                <StatusBadge status={handover.status} />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Employee:</span>
                  <span className="text-sm text-gray-900">
                    {handover.employee?.firstName 
                      ? `${handover.employee.firstName} ${handover.employee.lastName}`
                      : "N/A"}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Mileage:</span>
                  <span className="text-sm text-gray-900">{handover.mileage || "N/A"} km</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Fuel Level:</span>
                  <span className="text-sm text-gray-900">
                    {handover.fuelLevel 
                      ? handover.fuelLevel.charAt(0).toUpperCase() + handover.fuelLevel.slice(1) 
                      : "N/A"}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-sm text-gray-600">Exterior:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {handover.condition?.exterior 
                        ? handover.condition.exterior.charAt(0).toUpperCase() + handover.condition.exterior.slice(1)
                        : "N/A"}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-600">Interior:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {handover.condition?.interior
                        ? handover.condition.interior.charAt(0).toUpperCase() + handover.condition.interior.slice(1)
                        : "N/A"}
                    </span>
                  </div>
                </div>
                
                {handover.notes && (
                  <div>
                    <span className="text-sm text-gray-600">Notes:</span>
                    <p className="text-sm text-gray-900 mt-1 bg-white p-2 rounded border border-gray-200">
                      {handover.notes}
                    </p>
                  </div>
                )}
                
                {handover.damages && handover.damages.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600">Damages:</span>
                    <ul className="mt-1 space-y-1">
                      {handover.damages.map((damage, index) => (
                        <li key={index} className="text-sm text-gray-900 bg-white p-2 rounded border border-gray-200">
                          {damage.description || "No description provided"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Media Files Preview */}
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    {handover.photos && handover.photos.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {handover.photos.length} Photos
                      </span>
                    )}
                    
                    {handover.videos && handover.videos.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {handover.videos.length} Videos
                      </span>
                    )}
                    
                    {handover.voiceNotes && handover.voiceNotes.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {handover.voiceNotes.length} Voice Notes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// AADE Tab
const AADETab = ({ rental }) => (
  <div className="space-y-6">
    <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <FaFileInvoiceDollar className="mr-2 text-blue-600" />
        AADE Document Status
      </h3>
      
      {!rental.aade ? (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
          <p className="text-yellow-700">No AADE information available for this rental.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatusCard 
              title="Document Submitted" 
              isCompleted={rental.aade.submitted}
              date={rental.aade.submissionDate}
            />
            
            <StatusCard 
              title="Document Updated" 
              isCompleted={rental.aade.updated}
              date={rental.aade.updateDate}
            />
            
            <StatusCard 
              title="Document Completed" 
              isCompleted={rental.aade.completed}
              date={rental.aade.completionDate}
            />
          </div>
          
          {rental.aade.submitted && (
            <div className="mt-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Document ID:</span>
                <span className="text-sm font-medium text-gray-900">{rental.aade.dclId || "N/A"}</span>
              </div>
              
              {rental.aade.updated && (
                <div className="flex justify-between mt-2">
                  <span className="text-sm text-gray-600">Updated Document ID:</span>
                  <span className="text-sm font-medium text-gray-900">{rental.aade.updateDclId || "N/A"}</span>
                </div>
              )}
            </div>
          )}
          
          {rental.aade.errors && rental.aade.errors.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Errors ({rental.aade.errors.length})
              </h4>
              <div className="max-h-60 overflow-y-auto bg-red-50 p-3 rounded-lg border border-red-200">
                <ul className="space-y-2">
                  {rental.aade.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-700 p-2 bg-white rounded border border-red-100">
                      {error.message || JSON.stringify(error)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

// Helper Components
const StatusBadge = ({ status }) => {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-800';
  
  switch (status) {
    case 'pending':
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      break;
    case 'confirmed':
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      break;
    case 'completed':
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      break;
    case 'cancelled':
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      break;
    case 'disputed':
      bgColor = 'bg-orange-100';
      textColor = 'text-orange-800';
      break;
    default:
      break;
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const OptionBadge = ({ label, isSelected }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    isSelected 
      ? 'bg-blue-100 text-blue-800' 
      : 'bg-gray-100 text-gray-500'
  }`}>
    {isSelected ? (
      <FaCheckCircle className="mr-1 h-3 w-3" />
    ) : (
      <FaTimesCircle className="mr-1 h-3 w-3" />
    )}
    {label}
  </span>
);

const StatusCard = ({ title, isCompleted, date }) => (
  <div className={`p-4 rounded-lg border ${
    isCompleted ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200'
  }`}>
    <div className="flex items-center justify-between">
      <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      {isCompleted ? (
        <FaCheckCircle className="h-5 w-5 text-green-600" />
      ) : (
        <FaTimesCircle className="h-5 w-5 text-gray-400" />
      )}
    </div>
    
    {date && (
      <p className="text-xs text-gray-600 mt-2">
        {moment(date).format('MMM D, YYYY - HH:mm')}
      </p>
    )}
  </div>
);

export default RentalDetailsModal;
